import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import {STAGE_RUN_STATUS, STAGE_RUN_STATUS_VIRTUAL} from 'workflows/constants/StageRunConstants';

import {POSITION_SHAPE} from './GraphLayout';
import {HighlightMode} from './StageFlowNodeHighlightUtil';

function getArc({
  rx,
  ry,
  xAxisRotation,
  largeArcFlag,
  sweepFlag,
  x,
  y,
}: {
  rx: number;
  ry: number;
  xAxisRotation: number;
  largeArcFlag: number;
  sweepFlag: number;
  x: number;
  y: number;
}): string {
  return `A ${rx} ${ry} ${xAxisRotation} ${largeArcFlag} ${sweepFlag} ${x} ${y}`;
}

interface StageRunLinkVisualizationProps {
  highlightMode: HighlightMode;
  isDiscarded: boolean;
  onClick?: (e: React.MouseEvent<SVGPathElement>) => void;
  /**
   * leftPosition should be above and to the left of rightPosition.
   * If point L is at leftPosition and R is at rightPosition, the following figure is drawn,
   * with a rounded corner:
   *
   *      L
   *      |
   *      |
   *      |_________R
   */
  leftPosition: {x: number; y: number};
  rightPosition: {x: number; y: number};
}

export function StageRunLinkVisualization({
  highlightMode,
  isDiscarded,
  onClick,
  leftPosition,
  rightPosition,
}: StageRunLinkVisualizationProps): JSX.Element {
  assertAndContinue(leftPosition.x <= rightPosition.x, 'Expected leftPosition to be left of rightPosition');
  assertAndContinue(leftPosition.y <= rightPosition.y, 'Expected leftPosition to be above rightPosition');
  const borderRadius = Math.min(4, rightPosition.x - leftPosition.x, rightPosition.y - leftPosition.y);
  const arc = getArc({
    rx: borderRadius,
    ry: borderRadius,
    xAxisRotation: 0,
    largeArcFlag: 0,
    sweepFlag: 0,
    x: leftPosition.x + borderRadius,
    y: rightPosition.y,
  });

  const d = `
    M ${leftPosition.x} ${leftPosition.y}
    V ${rightPosition.y - borderRadius}
    ${arc}
    H ${rightPosition.x}
  `;

  return (
    <g>
      <path
        className={classNames('stageRunLinkVisualization-line', {
          'stageRunLinkVisualization-line--discarded': isDiscarded,
          'is-dehighlighted': highlightMode === HighlightMode.DEHIGHLIGHTED,
        })}
        d={d}
      />
      {onClick && <path className="stageRunLinkVisualization-clickTarget" d={d} onClick={onClick} />}
    </g>
  );
}

StageRunLinkVisualization.propTypes = {
  highlightMode: PropTypes.oneOf(Object.values(HighlightMode)).isRequired,
  isDiscarded: PropTypes.bool.isRequired,
  onClick: PropTypes.func,
  /**
   * leftPosition should be above and to the left of rightPosition.
   * If point L is at leftPosition and R is at rightPosition, the following figure is drawn,
   * with a rounded corner:
   *
   *      L
   *      |
   *      |
   *      |_________R
   */
  leftPosition: POSITION_SHAPE,
  rightPosition: POSITION_SHAPE,
};

interface StageRunCircleProps {
  className?: string;
  highlightMode: HighlightMode;
  isHovered: boolean;
  position: {x: number; y: number};
  radius: number;
  stageRunStatus: STAGE_RUN_STATUS | typeof STAGE_RUN_STATUS_VIRTUAL;
}

export function StageRunCircle(props: StageRunCircleProps): JSX.Element {
  const {className, stageRunStatus, position, radius, highlightMode, isHovered} = props;
  const halfRadius = radius / 2;
  return (
    <g>
      <circle
        className={classNames(className, 'stageRunVisualization', {
          'stageRunVisualization--initialized': stageRunStatus === STAGE_RUN_STATUS.INITIALIZED,
          'stageRunVisualization--completed': stageRunStatus === STAGE_RUN_STATUS.COMPLETED,
          'stageRunVisualization--discarded': stageRunStatus === STAGE_RUN_STATUS.DISCARDED,
          'stageRunVisualization--virtual': stageRunStatus === STAGE_RUN_STATUS_VIRTUAL,
          'is-dehighlighted': highlightMode === HighlightMode.DEHIGHLIGHTED,
          'is-hovered': isHovered,
        })}
        cx={position.x}
        cy={position.y}
        r={radius}
      />
      {(() => {
        switch (stageRunStatus) {
          case STAGE_RUN_STATUS.DISCARDED:
            return (
              <path
                className={classNames(
                  className,
                  'stageRunVisualization--discarded',
                  'stageRunVisualization-x'
                )}
                d={`M ${position.x + halfRadius} ${position.y + halfRadius}
                    L ${position.x - halfRadius} ${position.y - halfRadius}
                    M ${position.x - halfRadius} ${position.y + halfRadius}
                    L ${position.x + halfRadius} ${position.y - halfRadius}`}
              />
            );
          case STAGE_RUN_STATUS.COMPLETED:
            return (
              <path
                className="stageRunVisualization-check"
                d={`M ${position.x - halfRadius} ${position.y}
                    L ${position.x - radius / 3} ${position.y + (radius * 3) / 8}
                    L ${position.x + halfRadius} ${position.y - (radius * 3) / 8}`}
              />
            );
          default:
            // STAGE_RUN_STATUS.INITIALIZED:
            return null;
        }
      })()}
    </g>
  );
}

StageRunCircle.propTypes = {
  className: PropTypes.string,
  highlightMode: PropTypes.oneOf(Object.values(HighlightMode)).isRequired,
  isHovered: PropTypes.bool.isRequired,
  position: POSITION_SHAPE.isRequired,
  radius: PropTypes.number.isRequired,
  stageRunStatus: PropTypes.oneOf([...Object.values(STAGE_RUN_STATUS), STAGE_RUN_STATUS_VIRTUAL]).isRequired,
};

StageRunCircle.defaultProps = {
  className: 'stageFlowNodeVisualization--default',
};
